import { useContext, useEffect, useRef, useState, type MouseEvent } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { User as UserIcon, X, ChevronDown } from "lucide-react"
import { UserContext } from "./UserContext"
import { postLogout } from "../api/client"

/** The hamburger, cut to a honeycomb. Three equal rules say "menu" and
 *  nothing else; these five are the horizontal chords of a hexagon — 6
 *  units at the point, 14 at the shoulders, 20 across the middle — so the
 *  button carries the hex the rest of the app is built from (CombFill, the
 *  activity calendar's cells) at a glance. Five rather than three because
 *  three chords over a 24-unit box leave the glyph reading small: the
 *  extra pair fills the height and gives the taper enough steps to be a
 *  shape rather than two stubs. Drawn by hand rather than with lucide's
 *  Menu, whose three rules are equal by definition; the geometry otherwise
 *  matches lucide's — a 24-unit box, 2-wide round strokes in currentColor.
 *  It renders at 28 rather than 24, a size up on the avatar beside it: the
 *  chords are already at the edges of their box, so the box is the only
 *  thing left to grow. Thinner strokes were the other option and read
 *  fussy — five rules this close want the weight to stay a shape. */
const CombMenuIcon = () => (
    <svg
        width={28}
        height={28}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        aria-hidden="true"
    >
        <path d="M9 3h6" />
        <path d="M5 7.5h14" />
        <path d="M2 12h20" />
        <path d="M5 16.5h14" />
        <path d="M9 21h6" />
    </svg>
)

const MENU_CLOSE_TO_NAVIGATE_MS = 200

const NavigationBar = () => {
    const { user } = useContext(UserContext)
    const [isNavOpen, setIsNavOpen] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    const location = useLocation()

    const toggleNav = () => {
        setIsNavOpen(!isNavOpen)
    }
    
    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen)
    const navigate = useNavigate()

    // Menu links close their menu and navigate a moment later, so the page
    // is painted with the menu shut before the URL changes. The iOS shell's
    // back swipe shows WebKit's snapshot of the page as it navigated away;
    // with the location effect below doing the closing, that snapshot had
    // the menu open. Two animation frames were not enough for the shut menu
    // to reach the snapshot (measured on a phone, 2026-09-29); 200ms is.
    const closeThenNavigate = (e: MouseEvent<HTMLAnchorElement>) => {
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        const to = e.currentTarget.getAttribute("href")
        if (!to) return
        setIsNavOpen(false)
        setIsDropdownOpen(false)
        setTimeout(() => navigate(to), MENU_CLOSE_TO_NAVIGATE_MS)
    }

    const [showDevControls, setShowDevControls] = useState(false)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.metaKey && event.key.toLowerCase() === "d") {
                event.preventDefault()
                setShowDevControls(prev => !prev)
            }
        }
        window.addEventListener("keydown", handleKeyPress)
        return () => window.removeEventListener("keydown", handleKeyPress)
    }, [])


    // Close mobile nav when location changes
    useEffect(() => {
        setIsNavOpen(false)
    }, [location.pathname])

    const handleLogout = async () => {
        try {
            await postLogout()
        } catch (error) {
            console.error("Logout failed:", error)
        }
        // Hard-reload to `/login` so any logged-in page that's currently
        // mounted (e.g. CheckinsPage on `/`, which is registered as a
        // "public" route but only renders meaningfully for an authed user)
        // doesn't sit around with stale state. Mirrors the 401 interceptor.
        window.location.href = `${window.location.protocol}//${window.location.host}/login`
    }

    const isActive = (path: string) => {
        if (path === "/") {
            return location.pathname === "/"
        }
        return location.pathname.startsWith(path)
    }

    return (
        <>
            <nav className="bg-blush shadow shell-safe-top">
                {/* Full-bleed, not the max-w-7xl content column: this puts the
                    wordmark on the same 16px gutter as the hero chart's own title
                    and its month controls, at every width. */}
                <div className="w-full px-4">
                    <div className="flex justify-between h-16">
                        <div className="flex">
                            <div className="flex-shrink-0 flex items-center">
                                <Link to="/" className="font-serif text-2xl tracking-tight text-plum">Honeyguide</Link>
                            </div>
                        </div>
                        
                        {/* Desktop menu */}
                        <div className="hidden sm:ml-6 sm:flex sm:items-center">
                            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                                <Link
                                    to="/dashboard"
                                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                        isActive("/dashboard")
                                            ? "border-b-2 border-amber-500 text-gray-900"
                                            : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                    }`}
                                >
                                    Dash
                                </Link>
                                
                                
                                {user && (
                                    <>
                                        {user.isAdmin && (
                                        <Link
                                            to="/genie"
                                            onClick={(e) => {
                                                // If already on a genie page with a chat ID, force navigation to start new chat
                                                if (location.pathname.startsWith("/genie/")) {
                                                    e.preventDefault()
                                                    window.location.href = "/genie"
                                                }
                                            }}
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/genie")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Genie
                                        </Link>
                                        )}
                                        <Link
                                            to="/chats"
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/chats")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Chats
                                        </Link>
                                        <Link
                                            to="/projects"
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/projects")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Projects
                                        </Link>
                                        <Link
                                            to="/checkins"
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/checkins")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Checkins
                                        </Link>
                                        <Link
                                            to="/collections"
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/collections")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Collections
                                        </Link>
                                        <Link
                                            to="/squad"
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/squad")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Squad
                                        </Link>
                                        <Link
                                            to="/advice"
                                            className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                isActive("/advice")
                                                    ? "border-b-2 border-amber-500 text-gray-900"
                                                    : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Advice
                                        </Link>
                                        {user?.isAdmin && (
                                            <Link
                                                to="/admin"
                                                className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                                                    isActive("/admin")
                                                        ? "border-b-2 border-amber-500 text-gray-900"
                                                        : "border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                                                }`}
                                            >
                                                Admin
                                            </Link>
                                        )}
                                    </>
                                )}
                            </div>
                            
                            {/* Profile dropdown */}
                            {user && <>
                                <div className="ml-3 relative" ref={dropdownRef}>
                                    <div>
                                        <button
                                            onClick={toggleDropdown}
                                            className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-action"
                                        >
                                            {user.profilePicURL ? (
                                                <img
                                                    src={user.profilePicURL}
                                                    alt="Profile"
                                                    className="h-8 w-8 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="h-8 w-8 rounded-full bg-white/70 flex items-center justify-center">
                                                    <UserIcon size={16} className="text-gray-500" />
                                                </div>
                                            )}
                                            <ChevronDown size={16} className={`ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                    </div>
                                    
                                    {/* Dropdown menu */}
                                    {isDropdownOpen && (
                                        <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black/5 focus:outline-none z-50">
                                            <div className="py-1">
                                                <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                                                    <div className="font-medium">{user.firstName}</div>
                                                    <div className="text-gray-500 truncate">{user.email}</div>
                                                </div>
                                                <Link
                                                    to="/account"
                                                    onClick={closeThenNavigate}
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    Account
                                                </Link>
                                                <Link
                                                    onClick={handleLogout}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    to=""
                                                >
                                                    Log Out
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>}
                            
                        </div>
                        
                        {/* Mobile menu button */}
                        <div className="flex items-center sm:hidden">
                            <button
                                onClick={toggleNav}
                                aria-label={isNavOpen ? "Close menu" : "Open menu"}
                                aria-expanded={isNavOpen}
                                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-action"
                            >
                                {isNavOpen ? (
                                    <X size={24} aria-hidden="true" />
                                ) : (
                                    <CombMenuIcon />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Mobile menu */}
                {isNavOpen && (
                    <div className="sm:hidden">
                        <div className="pt-2 pb-3 space-y-1">
                            <Link
                                to="/dashboard"
                                onClick={closeThenNavigate}
                                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                    isActive("/dashboard")
                                        ? "border-amber-500 text-amber-900 bg-white/70"
                                        : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                }`}
                            >
                                Dash
                            </Link>
                            
                            {user?.isAdmin && (
                                <Link
                                    to="/genie"
                                    onClick={(e) => {
                                        // If already on a genie page with a chat ID, force navigation to start new chat
                                        if (location.pathname.startsWith("/genie/")) {
                                            e.preventDefault()
                                            window.location.href = "/genie"
                                            return
                                        }
                                        closeThenNavigate(e)
                                    }}
                                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                        isActive("/genie")
                                            ? "border-amber-500 text-amber-900 bg-white/70"
                                            : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                    }`}
                                >
                                    Genie
                                </Link>
                            )}

                            {user && (
                                <Link
                                    to="/chats"
                                    onClick={closeThenNavigate}
                                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                        isActive("/chats")
                                            ? "border-amber-500 text-amber-900 bg-white/70"
                                            : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                    }`}
                                >
                                    Chats
                                </Link>
                            )}
                            
                            {user && (
                                <>
                                    <Link
                                        to="/projects"
                                        onClick={closeThenNavigate}
                                        className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                            isActive("/projects")
                                                ? "border-amber-500 text-amber-900 bg-white/70"
                                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                        }`}
                                    >
                                        Projects
                                    </Link>
                                    <Link
                                        to="/checkins"
                                        onClick={closeThenNavigate}
                                        className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                            isActive("/checkins")
                                                ? "border-amber-500 text-amber-900 bg-white/70"
                                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                        }`}
                                    >
                                        Checkins
                                    </Link>
                                    <Link
                                        to="/collections"
                                        onClick={closeThenNavigate}
                                        className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                            isActive("/collections")
                                                ? "border-amber-500 text-amber-900 bg-white/70"
                                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                        }`}
                                    >
                                        Collections
                                    </Link>
                                    <Link
                                        to="/squad"
                                        onClick={closeThenNavigate}
                                        className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                            isActive("/squad")
                                                ? "border-amber-500 text-amber-900 bg-white/70"
                                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                        }`}
                                    >
                                        Squad
                                    </Link>
                                    <Link
                                        to="/advice"
                                        onClick={closeThenNavigate}
                                        className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                            isActive("/advice")
                                                ? "border-amber-500 text-amber-900 bg-white/70"
                                                : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                        }`}
                                    >
                                        Advice
                                    </Link>
                                    {user?.isAdmin && (
                                        <Link
                                            to="/admin"
                                            onClick={closeThenNavigate}
                                            className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                                                isActive("/admin")
                                                    ? "border-amber-500 text-amber-900 bg-white/70"
                                                    : "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                            }`}
                                        >
                                            Admin
                                        </Link>
                                    )}
                                </>
                            )}
                            
                        </div>
                        
                        {user && (
                            <div className="pt-4 pb-3 border-t border-gray-200">
                                <div className="flex items-center px-4">
                                    {user.profilePicURL ? (
                                        <img
                                            src={user.profilePicURL}
                                            alt="Profile"
                                            className="h-10 w-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="h-10 w-10 rounded-full bg-white/70 flex items-center justify-center">
                                            <UserIcon size={20} className="text-gray-500" />
                                        </div>
                                    )}
                                    <div className="ml-3">
                                        <div className="text-base font-medium text-gray-800">{user.firstName}</div>
                                        <div className="text-sm font-medium text-gray-500">{user.email}</div>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1">
                                    <Link
                                        to="/account"
                                        onClick={closeThenNavigate}
                                        className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                                    >
                                        Account
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                                    >
                                    Log Out
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </nav>
            
            
            {showDevControls && (
                <div className="fixed bottom-4 right-4 z-50">
                </div>
            )}
        </>
    )
}

export default NavigationBar